import React, { useState, useRef, useContext } from 'react';
import T from 'i18n-react';
import DialogContentText from '@material-ui/core/DialogContentText';
import AppDialog from '../../../core/Dialog/AppDialog';
import useIsMounted from 'ismounted';
import { useSnackbar } from 'notistack';
import { fetchJson } from '../../../core/fetch';
import { API_V1_PLANS_FAVOR, API_V1_PLANS_DISFAVOR } from '../../routing';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { AUTH_PARAM_KEYS, ACTION_VALUE } from '~/models/Auth/AuthParamModel';
import RoundButton from './components/RoundButton';
import SquareButton from './components/SquareButton';
import {
  AuthDialogContext,
  AuthParams,
} from '../../components/AuthDialog/AuthDialogContextProvider';
import { APP_VIEW_BUSINESSES } from '../../routing';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: '32px 10px',
    borderBottom: 'none',
    marginBottom: 45,
    backgroundColor: '#FDF9EA',
    [theme.breakpoints.up(768)]: {
      padding: '0px 0px 20px 0px',
      borderBottom: 'solid 1px #BEBEBE',
      marginBottom: 20,
      backgroundColor: theme.palette.common.white,
    },
  },
}));

type FavoritePlansProps = {
  planId: number;
  planName: string;
  isFavorite?: boolean;
  favoriteCount?: number;
  buttonType?: 'square' | 'rounded';
  isAuthenticated?: boolean;
  onChange?: (id: number, favorite: boolean, favoriteCount: number) => void;
};

const FavoritePlan = ({
  planId,
  planName,
  isFavorite: isFavoriteProps,
  favoriteCount: favoriteCountProps,
  buttonType,
  isAuthenticated,
  onChange,
}: FavoritePlansProps) => {
  const classes = useStyles();

  const [state, setState] = useState({
    favoriteCount: favoriteCountProps,
    isFavorite: isFavoriteProps,
    mouseEnter: false,
    mouseLeft: true,
  });
  const { handleOpen: handleOpenAuthDialog } = useContext(AuthDialogContext);
  const isMounted = useIsMounted();
  const alertDialogRef = useRef<typeof AppDialog>();

  const { enqueueSnackbar } = useSnackbar();
  const { isFavorite, favoriteCount, mouseEnter, mouseLeft } = state;

  const authParams: AuthParams = {
    [AUTH_PARAM_KEYS.ACTION]: ACTION_VALUE.FAVORITE,
    [AUTH_PARAM_KEYS.PLAN_ID]: planId,
  };

  const updateState = (data = {}) =>
    setState((prevState) => ({ ...prevState, ...data }));

  const switchFavorite = (
    url: string,
    isFavorite: boolean,
    onSuccess?: () => void,
  ) => {
    fetchJson(url, {
      init: { method: 'POST' },
      onSuccess: ({ json: { favoriteCount } }) => {
        if (isMounted.current) {
          onSuccess?.();
          updateState({
            favoriteCount,
            isFavorite,
            mouseEnter: false,
            mouseLeft: false,
          });
          onChange?.(planId, isFavorite, favoriteCount);
        }
      },
      onError: ({ messages = [T.translate('request.error')] }) =>
        isMounted.current &&
        messages.forEach((m) => enqueueSnackbar(m, { variant: 'error' })),
    });
  };

  const handleDisfavor = (event, onSuccess, onError) => {
    onSuccess();
    switchFavorite(API_V1_PLANS_DISFAVOR.replace(':id', planId), false);
  };

  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (isAuthenticated) {
      if (isFavorite) {
        alertDialogRef.current?.handleOpen?.();
      } else {
        switchFavorite(API_V1_PLANS_FAVOR.replace(':id', planId), true, () =>
          enqueueSnackbar(T.translate('plan.favored'), { variant: 'success' }),
        );
      }
    } else {
      handleOpenAuthDialog({ authParams });
    }
  };

  const handleMouseEnter = () =>
    isFavorite && mouseLeft && updateState({ mouseEnter: true });

  const handleMouseLeave = () =>
    isFavorite && updateState({ mouseEnter: false, mouseLeft: true });

  const isMouseEntered = isFavorite && mouseEnter && mouseLeft;
  const isNotAppView = !window.location.pathname.includes(APP_VIEW_BUSINESSES);

  return (
    <>
      {isNotAppView && (
        <>
          {buttonType === 'square' ? (
            <div className={classes.container}>
              <SquareButton
                favorite={isFavorite}
                isMouseEntered={isMouseEntered}
                mouseEnter={mouseEnter}
                onClick={handleClick}
                onMouseLeave={handleMouseLeave}
                onMouseEnter={handleMouseEnter}
                totalCount={favoriteCount}
                component="div"
              />
            </div>
          ) : (
            <RoundButton
              onClick={handleClick}
              favorite={isFavorite}
              component="div"
            />
          )}
        </>
      )}
      <AppDialog
        title={T.translate('plan.disfavorDialog.title', { name: planName })}
        contentNode={
          <DialogContentText>
            {T.translate('plan.disfavorDialog.content')}
          </DialogContentText>
        }
        onAccept={handleDisfavor}
        onClose={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        DialogProps={{ maxWidth: 500 }}
        actionButtonText={T.translate('plan.disfavor')}
        cancelButtonText={T.translate('cancel')}
        ActionButtonProps={{ variant: 'text' }}
        ref={alertDialogRef}
      />
    </>
  );
};

export default FavoritePlan;
